import React, { useState, useEffect, useMemo } from "react";
import {
  Input,
  Row,
  Card,
  Table,
  Spin,
  Button,
  Space,
  Form,
  Pagination,
} from "antd";
import { ArrowDownOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { connect, useDispatch } from "react-redux";

import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";
import cnt from "../../../services/definedConstant";
import { rightAction } from "../../../redux/actions";
import {
  labelConfig,
  staticConfig,
  apiConfig,
} from "../treasuryContractBooking.config";
import SearchLg from "@untitled-ui/icons-react/build/esm/SearchLg";
import ArrowLeft from "@untitled-ui/icons-react/build/esm/ArrowLeft";
import ArrowRight from "@untitled-ui/icons-react/build/esm/ArrowRight";
import { BOM } from "../../../services/definedMethod";

// Converts [2026, 3, 16] → "2026-03-16"
const formatDateArray = (arr) => {
  if (!Array.isArray(arr) || arr.length < 3) return "-";
  const [y, m, d] = arr;
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
};

// Status badge colours — keyed by statusDescription
const STATUS_COLORS = {
  Created: { bg: "#FFFAEB", color: "#B54708" },
  Open: { bg: "#ECFDF3", color: "#027A48" },
  Closed: { bg: "#FEF3F2", color: "#B42318" },
  Published: { bg: "#EFF8FF", color: "#175CD3" },
  Cancelled: { bg: "#F2F4F7", color: "#344054" },
};

const StatusBadge = ({ label }) => {
  const style = STATUS_COLORS[label] || { bg: "#F2F4F7", color: "#344054" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 99,
        fontSize: 12,
        fontWeight: 600,
        background: style.bg,
        color: style.color,
      }}
    >
      {label}
    </span>
  );
};

// ── Component ──────────────────────────────────────────────────────────────
const ManageAuction = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { userData, rightBtn, rightAction } = props;
  const { session } = userData || {};

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [originalList, setOriginalList] = useState([]);
  const [loading, setLoading] = useState(false);

  const pageSize = 10;

  const columns = [
    {
      title: (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          Auction Date{" "}
          <ArrowDownOutlined style={{ fontSize: 14, color: "gray" }} />
        </div>
      ),
      dataIndex: "auctionDate",
      sorter: (a, b) => a.auctionDate.localeCompare(b.auctionDate),
    },
    {
      title: "Settlement Date",
      dataIndex: "settlementDate",
    },
    {
      title: "Auction Code",
      dataIndex: "auctionCode",
    },
    {
      title: "Instrument Type",
      dataIndex: "instrumentType",
    },
    {
      title: "Currency",
      dataIndex: "currencyCode",
    },
    {
      title: "Issuing Authority",
      dataIndex: "issuingAuthority",
    },
    {
      title: "Status",
      dataIndex: "statusDescription",
      render: (val) => <StatusBadge label={val} />,
    },
  ];

  useEffect(() => {
    getAuctionList();
  }, []);

  useEffect(() => {
    if (BOM.HasPermission?.("CREATE_FINANCIAL_INSTRUMENT")) {
      rightAction(cnt.RIGHT_ADD_BTN);
    }
  }, []);

  const getAuctionList = () => {
    setLoading(true);

    const body = { session };
    const url = `${apiConfig.GetAuctions}/auctions`;

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.defaultUserMessage || "Failed to load auctions",
        );
        setLoading(false);
        return;
      }

      const mapped = (res || []).map((el) => ({
        key: el.auctionId,
        id: el.auctionId,
        auctionCode: el.auctionCode,
        auctionDate: formatDateArray(el.auctionDate),
        settlementDate: formatDateArray(el.settlementDate),
        instrumentType: el.instrumentType,
        currencyCode: el.currencyCode,
        issuingAuthority: el.issuingAuthority,
        status: el.status,
        statusDescription: el.statusDescription,
      }));

      setOriginalList(mapped);
      setSelectedRow([]);
      setCurrentPage(1);
      setLoading(false);
    });
  };

  const handleSearchInTable = (e) => {
    setSearchTerm(e.target.value || "");
    setCurrentPage(1);
  };

  const filteredData = useMemo(() => {
    if (!searchTerm) return originalList;
    const q = searchTerm.toLowerCase();
    return originalList.filter(
      (item) =>
        item.auctionDate?.toLowerCase().includes(q) ||
        item.auctionCode?.toLowerCase().includes(q) ||
        item.instrumentType?.toLowerCase().includes(q) ||
        item.currencyCode?.toLowerCase().includes(q) ||
        item.statusDescription?.toLowerCase().includes(q),
    );
  }, [originalList, searchTerm]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage]);

  const totalPages = Math.ceil(filteredData.length / pageSize);

  const itemRender = (_, type, el) => {
    if (type === "prev" || type === "next") return;
    return el;
  };

  const onSelectItemAction = (selectedRowKeys, selectedRows) => {
    if (!selectedRowKeys || selectedRowKeys.length !== 1) {
      setSelectedRow([]);
      dispatch({ type: cnt.CLEAR_RIGHT_BTN });
      if (BOM.HasPermission?.("CREATE_FINANCIAL_INSTRUMENT")) {
        dispatch({ type: cnt.RIGHT_ADD_BTN });
      }
      return;
    }

    setSelectedRow(selectedRows);

    if (BOM.HasPermission?.("READ_SECURITY_PRODUCT")) {
      dispatch({ type: cnt.RIGHT_DETAIL_BTN });
    }
    if (BOM.HasPermission?.("UPDATE_SECURITY_PRODUCT")) {
      dispatch({ type: cnt.RIGHT_EDIT_BTN });
    }
  };

  const handleOnView = () => {
    if (rightBtn?.detailBtn === "primary") {
      navigate("/view-auction", { state: selectedRow[0]?.id });
    }
  };

  const handleOnCreate = () => {
    if (rightBtn?.addBtn === "primary") {
      navigate("/add-auction");
    }
  };

  const handleOnEdit = () => {
    if (rightBtn?.editBtn === "primary") {
      navigate("/edit-auction", { state: selectedRow[0]?.id });
    }
  };

  return (
    <MainLayout
      isEnableRightSidebar
      onView={handleOnView}
      onAdd={handleOnCreate}
      onEdit={handleOnEdit}
      pageItems={staticConfig.manageAuction}
    >
      <TopContent
        pageTitle={labelConfig.auction}
        createPageTitle={labelConfig.auctionListing}
      />

      <Card
        style={{ border: "1px solid #EAECF0", borderRadius: 20, marginTop: 12 }}
      >
        <Spin spinning={loading}>
          <Row
            style={{
              marginBottom: 20,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Form form={form}>
              <Space size={12}>
                <Input
                  value={searchTerm}
                  onChange={handleSearchInTable}
                  placeholder="Search auctions…"
                  prefix={<SearchLg width={18} color="#667085" />}
                  style={{ width: 350, borderRadius: 10 }}
                />
              </Space>
            </Form>
          </Row>

          <Table
            rowSelection={{
              type: "checkbox",
              hideSelectAll: true,
              onChange: onSelectItemAction,
            }}
            columns={columns}
            dataSource={paginatedData}
            pagination={false}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 20,
            }}
          >
            <Button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            >
              <ArrowLeft /> Previous
            </Button>

            <Pagination
              current={currentPage}
              total={filteredData.length}
              pageSize={pageSize}
              showSizeChanger={false}
              itemRender={itemRender}
              onChange={setCurrentPage}
            />

            <Button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            >
              Next <ArrowRight />
            </Button>
          </div>
        </Spin>
      </Card>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  rightBtn: state.RightSideBtn,
});

export default connect(mapStateToProps, { rightAction })(ManageAuction);
